import { useState } from 'react';
import { Badge, Button, Form, Table } from 'react-bootstrap';

const inr = (v) => (v == null ? '-' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
const limit = (v) => (v == null ? 'Unlimited' : Number(v).toLocaleString('en-IN'));
const STORAGE_KEY = 'ibigdo.plans.showInactive';
const readPreference = () => { try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch (e) { return false; } };
const days = (v) => (v == null ? '-' : `${v} business day${v === 1 ? '' : 's'}`);

/**
 * System Admin > Subscription Plans: every plan side by side with everything that defines it (status, prices, limits, support, onboarding and each feature ticked or not),
 * so one look shows how the plans differ. Edit and delete sit under each plan's name. `featureCatalog` is the list of optional features ({key, label}).
 */
function SubscriptionPlansOverview({ plans, featureCatalog, onEdit, onDelete }) {
  const [showInactive, setShowInactive] = useState(readPreference);
  const inactive = plans.filter((p) => !p.isActive).length;
  // Retired plans only crowd the comparison, so they are folded away by default (unless there is nothing else to show)
  const hide = !showInactive && inactive > 0 && inactive < plans.length;
  const choose = (on) => {
    setShowInactive(on);
    try { localStorage.setItem(STORAGE_KEY, on ? '1' : '0'); } catch (e) { /* a convenience only */ }
  };
  const sorted = [...plans].filter((p) => !hide || p.isActive).sort((a, b) => Number(a.priceYearly) - Number(b.priceYearly) || String(a.name).localeCompare(String(b.name)));
  const rows = [
    ['Trial', (p) => (p.trialDays ? `${p.trialDays} days` : 'None')],
    ['Price per month', (p) => (Number(p.priceMonthly) > 0 ? inr(p.priceMonthly) : 'Yearly only')],
    ['Price per year', (p) => inr(p.priceYearly)],
    ['One-time onboarding', (p) => (p.onboardingFee == null || Number(p.onboardingFee) <= 0 ? 'None' : `${inr(p.onboardingFee)} (${p.onboardingRequired ? 'required' : 'optional'})`)],
    ['Warehouses', (p) => limit(p.maxWarehouses)],
    ['Production units', (p) => limit(p.maxProductionUnits)],
    ['Retail stores', (p) => limit(p.maxRetailStores)],
    ['Users', (p) => limit(p.maxUsers)],
    ['Documents a month', (p) => limit(p.maxDocumentsPerMonth)],
    ['Daily transactions', (p) => limit(p.maxDailyTransactions)],
    ['First reply from the account manager', (p) => days(p.accountManagerResponseDays)],
    ['iBigDo replies within, if they have not', (p) => days(p.supportResponseDays)],
    ['Who it is for', (p) => p.audience || '-'],
    ['Shown to account managers', (p) => (p.visibleToPartners ? 'Yes' : 'No')],
  ];
  const sticky = { position: 'sticky', left: 0, zIndex: 1, background: 'var(--bs-body-bg)' }; // the labels stay put while the plans scroll sideways
  const cell = (key, children, extra) => <td key={key} className={`text-center ${extra || ''}`}>{children}</td>;

  return (
    <div data-testid="subscription-plans-overview">
      {inactive > 0 && inactive < plans.length && (
        <Form.Check type="switch" id="show-inactive-plans" className="mb-2" checked={showInactive} onChange={(e) => choose(e.target.checked)}
          label={`Show inactive plans (${inactive})`} data-testid="show-inactive-plans" />
      )}
      <div className="table-responsive">
      <Table bordered size="sm" className="align-middle mb-0">
        <thead>
          <tr>
            <th style={{ minWidth: 190, ...sticky }} />
            {sorted.map((p) => (
              <th key={p.id} className="text-center" style={{ minWidth: 170 }}>
                <div className="fs-6">{p.name}</div>
                <div className="my-1">
                  <Badge bg={p.isActive ? 'success' : 'secondary'}>{p.isActive ? 'Active' : 'Inactive'}</Badge>
                  {p.isDefaultPlan && <Badge bg="info" className="ms-1">Default</Badge>}
                </div>
                {p.description && <div className="fw-normal text-muted small mb-1">{p.description}</div>}
                <Button size="sm" variant="outline-primary" className="me-1" title={`Edit ${p.name}`} aria-label={`Edit ${p.name}`} onClick={() => onEdit(p)}><i className="bi bi-pencil" /></Button>
                <Button size="sm" variant="outline-danger" title={`Delete ${p.name}`} aria-label={`Delete ${p.name}`} onClick={() => onDelete(p.id)}><i className="bi bi-trash" /></Button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <th scope="row" className="fw-normal text-muted" style={sticky}>{label}</th>
              {sorted.map((p) => cell(p.id, value(p)))}
            </tr>
          ))}
          <tr><th colSpan={sorted.length + 1} className="bg-body-tertiary small text-uppercase text-muted">Optional features</th></tr>
          {featureCatalog.map((f) => (
            <tr key={f.key}>
              <th scope="row" className="fw-normal" style={sticky}>{f.label}</th>
              {sorted.map((p) => cell(p.id, (p.features || []).includes(f.key)
                ? <i className="bi bi-check-lg text-success" aria-label="Included" />
                : <span className="text-muted" aria-label="Not included">&mdash;</span>))}
            </tr>
          ))}
        </tbody>
      </Table>
      </div>
    </div>
  );
}

export default SubscriptionPlansOverview;
